// CTW-DURMAH-STUDENT-PAINPOINTS-SEO-RESOURCE-EXPANSION-02:
// Student journey cluster article page: your first moot. Fully public.
// Content lives in the data module. Cross-links the public Speak Law page,
// which this guide is a natural on-ramp for.

import React from "react";
import Head from "next/head";
import { RelatedVideoLessons } from "@/components/resources/RelatedVideoLessons";
import {
  yourFirstMootSections,
  yourFirstMootSources,
} from "@/content/resources/yourFirstMoot";
import type { ResourceSection } from "@/content/resources/types";
import {
  ResourceBreadcrumbs,
  ResourceCrossLinks,
  ResourceDisclaimer,
  ResourceHero,
  ResourceSoftCta,
  SectionSourceLinks,
  SourcesSection,
} from "@/components/resources/ResourcePrimitives";

const SITE = "https://www.durmah.ai";

function SectionBlock({ section }: { section: ResourceSection }) {
  return (
    <section id={section.id} className="mb-14 scroll-mt-32">
      <h2 className="mb-4 text-2xl md:text-3xl font-black text-gray-900 dark:text-white">
        {section.heading}
      </h2>
      {section.paragraphs.map((p, i) => (
        <p key={i} className="mb-4 leading-relaxed text-gray-600 dark:text-slate-400">
          {p}
        </p>
      ))}
      {section.bullets && (
        <ul className="mb-4 space-y-3">
          {section.bullets.map((b, i) => (
            <li key={i} className="flex gap-3 leading-relaxed text-gray-600 dark:text-slate-400">
              <span aria-hidden className="mt-[10px] h-1.5 w-1.5 shrink-0 rounded-full bg-indigo-400" />
              <span>{b}</span>
            </li>
          ))}
        </ul>
      )}
      {section.callout && (
        <div className="mt-5 rounded-2xl border border-indigo-100 dark:border-indigo-500/20 bg-indigo-50/70 dark:bg-indigo-500/5 p-6">
          <p className="mb-1.5 text-sm font-black text-indigo-900 dark:text-indigo-300">
            {section.callout.title}
          </p>
          <p className="m-0 text-sm leading-relaxed text-indigo-900/80 dark:text-indigo-200/70">
            {section.callout.body}
          </p>
        </div>
      )}
      <SectionSourceLinks links={section.links} />
    </section>
  );
}

export default function YourFirstMootPage() {
  const canonical = `${SITE}/resources/your-first-moot`;
  const title = "Your First Moot: A Beginner's Guide for UK Law Students | Durmah";
  const description =
    "Everything a UK law student needs before a first moot: what a moot actually is, how one runs, where undergraduates really start, building a submission, knowing your authorities, etiquette, handling judicial questions, and what to do about the nerves.";

  const articleSchema = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "Article",
    headline: "Your First Moot: A Beginner's Guide for UK Law Students",
    description,
    url: canonical,
    isAccessibleForFree: true,
    author: { "@type": "Organization", name: "Durmah" },
    publisher: {
      "@type": "Organization",
      name: "Durmah",
      logo: { "@type": "ImageObject", url: `${SITE}/android-chrome-512x512.png` },
    },
    datePublished: "2026-07-10",
    dateModified: "2026-07-10",
  });

  const breadcrumbSchema = JSON.stringify({
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Student Resources", item: `${SITE}/resources` },
      { "@type": "ListItem", position: 2, name: "Your First Moot", item: canonical },
    ],
  });

  return (
    <div className="bg-white dark:bg-[#020617] min-h-screen flex flex-col pt-32">
      <Head>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link key="route-canonical" rel="canonical" href={canonical} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta key="route-og-url" property="og:url" content={canonical} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: articleSchema }} />
        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: breadcrumbSchema }} />
      </Head>

      <main className="flex-1 py-16 lg:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <ResourceBreadcrumbs current="Your First Moot" />
          <ResourceHero
            eyebrow="Student journey"
            title={
              <>
                Your First{" "}
                <span className="text-indigo-600 dark:text-indigo-400">Moot</span>
              </>
            }
            subtitle="The listing sites tell you which competitions exist. Almost nobody tells you how to actually do one. This guide covers the whole first-moot experience: how a moot runs, where undergraduates genuinely start, building a submission that survives questions, courtroom etiquette, and why shaking hands and a wobbly voice are part of everyone's first time."
          />

          {/* In-page navigation */}
          <nav aria-label="On this page" className="mb-12 rounded-2xl border border-gray-200 dark:border-slate-800 bg-gray-50 dark:bg-slate-900/40 p-6">
            <p className="mb-3 text-xs font-black uppercase tracking-widest text-gray-500 dark:text-slate-500">
              On this page
            </p>
            <ul className="grid grid-cols-1 gap-x-6 gap-y-1.5 sm:grid-cols-2">
              {yourFirstMootSections.map((s) => (
                <li key={s.id}>
                  <a
                    href={`#${s.id}`}
                    className="text-sm font-medium text-indigo-700 dark:text-indigo-400 hover:underline"
                  >
                    {s.heading}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          {yourFirstMootSections.map((section) => (
            <SectionBlock key={section.id} section={section} />
          ))}

          <RelatedVideoLessons
            heading="Practise answering the judge"
            intro="Watch a short method for handling a question honestly before returning to your submissions. Your competition rules remain in charge."
            slugs={["moot-answer-the-question"]}
          />
          <ResourceCrossLinks
            items={[
              {
                title: "Speak Law",
                description:
                  "Why speaking law aloud is a core Durmah discipline, and how the platform trains it.",
                href: "/speak-law",
              },
              {
                title: "How to Read a Case",
                description:
                  "Knowing your authorities properly: the briefing method a moot judge will test you on.",
                href: "/resources/how-to-read-a-case",
              },
              {
                title: "Answering Problem Questions",
                description:
                  "The same reasoning spine your submissions use, in its written exam form.",
                href: "/resources/answering-problem-questions",
              },
            ]}
          />

          <SourcesSection groups={yourFirstMootSources} />
          <ResourceSoftCta
            title="Want a practice partner before you stand up?"
            body="Durmah can pressure-test your grasp of the authorities you plan to cite and help you rehearse explaining your argument in plain speech. The skeleton, the speech and the composure stay yours. This guide stays free either way."
          />
          <ResourceDisclaimer />
        </div>
      </main>
    </div>
  );
}
